import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  TextField,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import {
  ArrowBack,
  Add,
  Delete,
  Person,
  Phone,
  Email,
  LocationOn,
} from '@mui/icons-material';
import { useNotification } from '../contexts/NotificationContext';

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

interface Lead {
  id: number;
  title: string;
  customer_id?: number;
  customer_name?: string;
  customer_first_name?: string;
  customer_last_name?: string;
  customer_email?: string;
  customer_phone?: string;
  customer_address_1?: string;
  customer_address_2?: string;
  customer_town?: string;
  customer_postcode?: string;
  source?: string;
  status?: string;
  priority?: string;
  estimated_value?: number;
  notes?: string;
  assigned_to?: number;
  date_required?: string;
  door_style?: string;
  worktop_style?: string;
  worktop_make?: string;
  worktop_manufacture?: string;
  worktop_thickness?: string;
  worktop_size?: string;
  item_colour?: string;
  back_colour?: string;
  introducer_id?: number;
  order_number?: string;
  fitter_name?: string;
  fitter_balance?: number;
  gas_fitter_name?: string;
  gas_fitter_balance?: number;
  material_cost?: number;
  deposit_amount?: number;
  order_detail_notes?: string;
}

interface OrderItem {
  id?: number;
  lead_id?: number;
  project_id?: number;
  supplier_id?: number;
  category_id?: number;
  supplier_name?: string;
  qty: string;
  hxw: string;
  type: string;
  l: string;
  drillings: string;
  r: string;
  description?: string;
  sq?: string;
  code?: string;
  colour?: string;
  style?: string;
  sort_order?: number;
  supplier_delivery_date?: string;
  note_ref?: number | null;
  isNew?: boolean;
  isDeleting?: boolean;
}

interface Project {
  id: number;
  title: string;
  customer_id?: number;
  customer_name?: string;
  customer_email?: string;
  customer_phone?: string;
  customer_address?: string;
  status?: string;
  budget?: number;
  notes?: string;
  assigned_to?: number;
  delivery_date?: string;
  door_style?: string;
  worktop_style?: string;
  worktop_make?: string;
  worktop_manufacture?: string;
  worktop_thickness?: string;
  worktop_size?: string;
  item_colour?: string;
  back_colour?: string;
  order_number?: string;
}

interface Supplier {
  id: number;
  company: string;
  primary_product?: string;
}

interface Category {
  id: number;
  name: string;
  sort_order: number;
}

interface Remedial {
  id: number;
  project_id: number;
  repair?: string;
  description?: string;
  location_of_materials?: string;
  customer_contact_notes?: string;
  date_invoice_paid?: string;
  whos_at_fault?: string;
  status: 'open' | 'resolved';
  fitter_cost: number;
  delivery_cost: number;
  material_cost: number;
  reported_date?: string;
  start_date?: string;
  resolved_date?: string;
  // Project-related fields (joined from projects table)
  project_title?: string;
  order_number?: string;
  customer_first_name?: string;
  customer_last_name?: string;
  customer_email?: string;
  customer_phone?: string;
  customer_address?: string;
}

interface OrderBuildProps {
  isProject?: boolean;
  isRemedial?: boolean;
}

export default function OrderBuild({ isProject = false, isRemedial = false }: OrderBuildProps) {
  console.log('OrderBuild component rendering', { isProject, isRemedial });
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { showNotification } = useNotification();

  const [lead, setLead] = useState<Lead | null>(null);
  const [project, setProject] = useState<Project | null>(null);
  const [remedial, setRemedial] = useState<Remedial | null>(null);
  const [orderItems, setOrderItems] = useState<OrderItem[]>([]);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [autoSaveTimeout, setAutoSaveTimeout] = useState<NodeJS.Timeout | null>(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Lead metadata states
  const [dateRequired, setDateRequired] = useState('');
  const [deliveryDate, setDeliveryDate] = useState('');
  const [customerDeliveryDate, setCustomerDeliveryDate] = useState('');
  const [doorStyle, setDoorStyle] = useState('');
  const [worktopStyle, setWorktopStyle] = useState('');
  const [worktopMake, setWorktopMake] = useState('');
  const [worktopManufacture, setWorktopManufacture] = useState('');
  const [worktopThickness, setWorktopThickness] = useState('');
  const [worktopSize, setWorktopSize] = useState('');
  const [itemColour, setItemColour] = useState('');
  const [backColour, setBackColour] = useState('');

  // Bulk delivery date setter
  const [bulkSupplier, setBulkSupplier] = useState<number | ''>('');
  const [bulkDate, setBulkDate] = useState('');

  // Order notes for asterisked items (dynamic - keyed by asterisk number)
  const [orderNotes, setOrderNotes] = useState<{[key: number]: string}>({});

  // Fetch lead/project/remedial data
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        console.log(`Fetching data for ${isRemedial ? 'remedial' : isProject ? 'project' : 'lead'}:`, id);

        let entityData;

        if (isRemedial) {
          // Fetch remedial details with project and customer info
          const remedialResponse = await axios.get(`${API_BASE_URL}/projects/remedials/${id}`, {
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
          });

          entityData = remedialResponse.data;
          console.log('Remedial data received:', entityData);
          setRemedial(entityData);
        } else if (isProject) {
          // Fetch project details
          const projectResponse = await axios.get(`${API_BASE_URL}/projects/${id}`, {
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
          });

          entityData = projectResponse.data.project || projectResponse.data;
          console.log('Project data received:', entityData);
          setProject(entityData);
        } else {
          // Fetch lead details
          const leadResponse = await axios.get(`${API_BASE_URL}/leads/${id}`, {
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
          });

          entityData = leadResponse.data.lead || leadResponse.data;
          console.log('Lead data received:', entityData);
          setLead(entityData);
        }
        
        // Set metadata fields - format dates to YYYY-MM-DD for input fields
        const formatDateForInput = (dateStr: string | null | undefined) => {
          if (!dateStr) return '';
          const date = new Date(dateStr);
          if (isNaN(date.getTime())) return '';
          return date.toISOString().split('T')[0];
        };

        if (isProject || isRemedial) {
          setDeliveryDate(formatDateForInput(entityData.delivery_date));
        } else {
          setDateRequired(formatDateForInput(entityData.date_required));
        }
        setCustomerDeliveryDate(formatDateForInput(entityData.customer_delivery_date));
        setDoorStyle(entityData.door_style || '');
        setWorktopStyle(entityData.worktop_style || '');
        setWorktopMake(entityData.worktop_make || '');
        setWorktopManufacture(entityData.worktop_manufacture || '');
        setWorktopThickness(entityData.worktop_thickness || '');
        setWorktopSize(entityData.worktop_size || '');
        setItemColour(entityData.item_colour || '');
        setBackColour(entityData.back_colour || '');
        // Load order notes from JSON field
        if (entityData.order_notes_json) {
          try {
            setOrderNotes(JSON.parse(entityData.order_notes_json));
          } catch (e) {
            setOrderNotes({});
          }
        }

        // Fetch suppliers
        const suppliersResponse = await axios.get(`${API_BASE_URL}/suppliers`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        console.log('Suppliers received:', suppliersResponse.data);
        setSuppliers(suppliersResponse.data);

        // Fetch categories
        const categoriesResponse = await axios.get(`${API_BASE_URL}/categories`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        console.log('Categories received:', categoriesResponse.data);
        setCategories(categoriesResponse.data);

        // Fetch order items
        const orderItemsUrl = isRemedial
          ? `${API_BASE_URL}/remedials/${id}/order-items`
          : isProject
            ? `${API_BASE_URL}/projects/${id}/order-items`
            : `${API_BASE_URL}/leads/${id}/order-items`;

        const itemsResponse = await axios.get(orderItemsUrl, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        console.log('Order items received:', itemsResponse.data);
        
        if (itemsResponse.data.length === 0) {
          // Add initial empty row if no items exist
          setOrderItems([createEmptyItem()]);
        } else {
          setOrderItems(itemsResponse.data);
        }
        
      } catch (error) {
        console.error('Error fetching data:', error);
        showNotification('Failed to load order data', 'error');
      } finally {
        setLoading(false);
      }
    };
    
    if (id) {
      fetchData();
    }
  }, [id, showNotification, isProject, isRemedial]);

  // Warn user if they try to leave with unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      // Clear any pending timeouts
      if (autoSaveTimeout) {
        clearTimeout(autoSaveTimeout);
      }
    };
  }, [autoSaveTimeout, hasUnsavedChanges]);

  // Create empty item
  const createEmptyItem = (): OrderItem => ({
    qty: '',
    hxw: '',
    type: '',
    l: '',
    drillings: '',
    r: '',
    description: '',
    isNew: true
  });

  // Check if category is Worktops
  const isWorktopCategory = (categoryId?: number): boolean => {
    if (!categoryId) return false;
    const category = categories.find(c => c.id === categoryId);
    return category?.name === 'Worktops';
  };

  // Auto-save lead metadata - removed useCallback to avoid stale closures
  const saveLeadMetadata = async (force: boolean = false) => {
    // Don't save if we're already saving (unless forced)
    if (saving && !force) return;

    // For projects, we don't need the project object to be loaded, just the ID
    if (isProject && id) {
      // Format the dates properly for MySQL (YYYY-MM-DD)
      const formattedDeliveryDate = deliveryDate ? deliveryDate.split('T')[0] : null;
      const formattedCustomerDeliveryDate = customerDeliveryDate ? customerDeliveryDate.split('T')[0] : null;

      console.log('Saving project metadata:', {
        delivery_date: formattedDeliveryDate,
        customer_delivery_date: formattedCustomerDeliveryDate,
        door_style: doorStyle,
        worktop_style: worktopStyle,
        worktop_make: worktopMake,
        worktop_manufacture: worktopManufacture,
        worktop_thickness: worktopThickness,
        worktop_size: worktopSize,
        item_colour: itemColour,
        back_colour: backColour
      });

      try {
        setSaving(true);
        const response = await axios.patch(
          `${API_BASE_URL}/projects/${id}`,
          {
            delivery_date: formattedDeliveryDate,
            customer_delivery_date: formattedCustomerDeliveryDate,
            door_style: doorStyle || null,
            worktop_style: worktopStyle || null,
            worktop_make: worktopMake || null,
            worktop_manufacture: worktopManufacture || null,
            worktop_thickness: worktopThickness || null,
            worktop_size: worktopSize || null,
            item_colour: itemColour || null,
            back_colour: backColour || null,
            order_notes_json: JSON.stringify(orderNotes)
          },
          {
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
          }
        );
        console.log('Project metadata saved successfully:', response.data);
        setHasUnsavedChanges(false);
        // Don't show notification on every save - too noisy
        // showNotification('Project details saved', 'success');
      } catch (error) {
        console.error('Error saving project metadata:', error);
        showNotification('Failed to save project details', 'error');
      } finally {
        setSaving(false);
      }
    } else if (lead && id) {
      console.log('Saving lead metadata with date_required:', dateRequired);
      
      try {
        await axios.put(
          `${API_BASE_URL}/leads/${id}`,
          {
            title: lead.title,
            customer_id: lead.customer_id,
            source: lead.source,
            status: lead.status,
            priority: lead.priority,
            estimated_value: lead.estimated_value,
            notes: lead.notes,
            assigned_to: lead.assigned_to,
            introducer_id: lead.introducer_id || null,
            order_number: lead.order_number || null,
            date_required: dateRequired || null,
            customer_delivery_date: customerDeliveryDate || null,
            door_style: doorStyle,
            worktop_style: worktopStyle,
            worktop_make: worktopMake,
            worktop_manufacture: worktopManufacture,
            worktop_thickness: worktopThickness,
            worktop_size: worktopSize,
            item_colour: itemColour,
            back_colour: backColour,
            order_notes_json: JSON.stringify(orderNotes),
            fitter_name: lead.fitter_name || null,
            fitter_balance: lead.fitter_balance || 0,
            gas_fitter_name: lead.gas_fitter_name || null,
            gas_fitter_balance: lead.gas_fitter_balance || 0,
            // Preserve the financial and order detail fields
            material_cost: lead.material_cost || 0,
            deposit_amount: lead.deposit_amount || 0,
            order_detail_notes: lead.order_detail_notes || ''
          },
          {
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
          }
        );
        console.log('Lead metadata saved');
        setHasUnsavedChanges(false);
      } catch (error) {
        console.error('Error saving lead metadata:', error);
        showNotification('Failed to save order details', 'error');
      }
    } else if (isRemedial && id) {
      // Format the dates properly for MySQL (YYYY-MM-DD)
      const formattedDeliveryDate = deliveryDate ? deliveryDate.split('T')[0] : null;
      const formattedCustomerDeliveryDate = customerDeliveryDate ? customerDeliveryDate.split('T')[0] : null;

      console.log('Saving remedial metadata:', {
        delivery_date: formattedDeliveryDate,
        customer_delivery_date: formattedCustomerDeliveryDate,
        door_style: doorStyle,
        worktop_style: worktopStyle,
        worktop_make: worktopMake,
        worktop_manufacture: worktopManufacture,
        worktop_thickness: worktopThickness,
        worktop_size: worktopSize,
        item_colour: itemColour,
        back_colour: backColour
      });

      try {
        setSaving(true);
        // Get the remedial's project_id first
        const remedialResponse = await axios.get(`${API_BASE_URL}/projects/remedials/${id}`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        const projectId = remedialResponse.data.project_id;

        const response = await axios.patch(
          `${API_BASE_URL}/projects/${projectId}/remedials/${id}`,
          {
            delivery_date: formattedDeliveryDate,
            customer_delivery_date: formattedCustomerDeliveryDate,
            door_style: doorStyle || null,
            worktop_style: worktopStyle || null,
            worktop_make: worktopMake || null,
            worktop_manufacture: worktopManufacture || null,
            worktop_thickness: worktopThickness || null,
            worktop_size: worktopSize || null,
            item_colour: itemColour || null,
            back_colour: backColour || null
          },
          {
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
          }
        );
        console.log('Remedial metadata saved successfully:', response.data);
        setHasUnsavedChanges(false);
      } catch (error) {
        console.error('Error saving remedial metadata:', error);
        showNotification('Failed to save remedial details', 'error');
      } finally {
        setSaving(false);
      }
    }
  };

  // Handle metadata changes with auto-save
  const handleMetadataChange = (field: string, value: any) => {
    console.log(`Field changed: ${field} = ${value}`);
    
    // Clear existing timeout
    if (autoSaveTimeout) {
      clearTimeout(autoSaveTimeout);
    }
    
    // Check which field changed and if value actually changed
    let valueChanged = false;
    
    // Update state
    switch (field) {
      case 'date_required':
        if (dateRequired !== value) {
          setDateRequired(value);
          valueChanged = true;
        }
        break;
      case 'delivery_date':
        if (deliveryDate !== value) {
          setDeliveryDate(value);
          valueChanged = true;
        }
        break;
      case 'customer_delivery_date':
        if (customerDeliveryDate !== value) {
          setCustomerDeliveryDate(value);
          valueChanged = true;
        }
        break;
      case 'door_style':
        if (doorStyle !== value) {
          setDoorStyle(value);
          valueChanged = true;
        }
        break;
      case 'worktop_style':
        if (worktopStyle !== value) {
          setWorktopStyle(value);
          valueChanged = true;
        }
        break;
      case 'worktop_make':
        if (worktopMake !== value) {
          setWorktopMake(value);
          valueChanged = true;
        }
        break;
      case 'worktop_manufacture':
        if (worktopManufacture !== value) {
          setWorktopManufacture(value);
          valueChanged = true;
        }
        break;
      case 'worktop_thickness':
        if (worktopThickness !== value) {
          setWorktopThickness(value);
          valueChanged = true;
        }
        break;
      case 'worktop_size':
        if (worktopSize !== value) {
          setWorktopSize(value);
          valueChanged = true;
        }
        break;
      case 'item_colour':
        if (itemColour !== value) {
          setItemColour(value);
          valueChanged = true;
        }
        break;
      case 'back_colour':
        if (backColour !== value) {
          setBackColour(value);
          valueChanged = true;
        }
        break;
    }
    
    // Only set auto-save if value actually changed
    if (valueChanged) {
      setHasUnsavedChanges(true);
      // Set new timeout for auto-save (1 second for quick saves)
      const timeout = setTimeout(() => {
        console.log(`Auto-saving after change to ${field}:`, value);
        saveLeadMetadata();
      }, 1000);
      setAutoSaveTimeout(timeout);
    }
  };

  // Immediately save when field loses focus
  const handleMetadataBlur = () => {
    if (hasUnsavedChanges && autoSaveTimeout) {
      clearTimeout(autoSaveTimeout);
      setAutoSaveTimeout(null);
      saveLeadMetadata();
    }
  };

  // Handle Done button - save everything before navigating
  const handleDone = async () => {
    console.log('Done button clicked - saving all changes');

    // Clear any pending auto-save
    if (autoSaveTimeout) {
      clearTimeout(autoSaveTimeout);
    }

    // Save metadata first (force save even if already saving) - but not for remedials
    if (!isRemedial) {
      await saveLeadMetadata(true);
    }

    // Navigate back
    if (isRemedial) {
      // For remedials, navigate back to the project detail page
      // We need to get the project_id from the remedial
      navigate(`/projects/${remedial?.project_id}`);
    } else {
      navigate(isProject ? `/projects/${id}` : `/leads/${id}/edit`);
    }
  };

  // Save order item
  const saveOrderItem = async (item: OrderItem, index: number) => {
    try {
      setSaving(true);

      if (item.isNew && !item.id) {
        // Create new item - use correct URL based on mode
        const createUrl = isRemedial
          ? `${API_BASE_URL}/remedials/${id}/order-items`
          : isProject
            ? `${API_BASE_URL}/projects/${id}/order-items`
            : `${API_BASE_URL}/leads/${id}/order-items`;

        const response = await axios.post(
          createUrl,
          {
            supplier_id: item.supplier_id,
            category_id: item.category_id,
            qty: item.qty,
            hxw: item.hxw,
            type: item.type,
            l: item.l,
            drillings: item.drillings,
            r: item.r,
            description: item.description || null,
            sq: item.sq || null,
            code: item.code || null,
            colour: item.colour || null,
            style: item.style || null,
            sort_order: index,
            supplier_delivery_date: item.supplier_delivery_date || null,
            note_ref: item.note_ref || null
          },
          {
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
          }
        );

        // Update item with ID from server
        const updatedItems = [...orderItems];
        updatedItems[index] = { ...response.data, isNew: false };
        setOrderItems(updatedItems);

      } else if (item.id) {
        // Update existing item
        const updateUrl = isRemedial
          ? `${API_BASE_URL}/remedials/${id}/order-items/${item.id}`
          : isProject
            ? `${API_BASE_URL}/projects/${id}/order-items/${item.id}`
            : `${API_BASE_URL}/leads/${id}/order-items/${item.id}`;

        await axios.put(
          updateUrl,
          {
            supplier_id: item.supplier_id,
            category_id: item.category_id,
            qty: item.qty,
            hxw: item.hxw,
            type: item.type,
            l: item.l,
            drillings: item.drillings,
            r: item.r,
            description: item.description || null,
            sq: item.sq || null,
            code: item.code || null,
            colour: item.colour || null,
            style: item.style || null,
            sort_order: index,
            supplier_delivery_date: item.supplier_delivery_date || null,
            note_ref: item.note_ref || null
          },
          {
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
          }
        );
      }
      
    } catch (error) {
      console.error('Error saving order item:', error);
      showNotification('Failed to save order item', 'error');
    } finally {
      setSaving(false);
    }
  };

  // Handle field change with auto-save
  const handleItemChange = (index: number, field: keyof OrderItem, value: any) => {
    const updatedItems = [...orderItems];
    updatedItems[index] = { ...updatedItems[index], [field]: value };
    setOrderItems(updatedItems);

    // Auto-save after 1 second of inactivity
    if (autoSaveTimeout) {
      clearTimeout(autoSaveTimeout);
    }

    const timeout = setTimeout(() => {
      saveOrderItem(updatedItems[index], index);
    }, 1000);
    setAutoSaveTimeout(timeout);
  };

  // Apply delivery date to all rows for a specific supplier
  const applyDeliveryDateToSupplier = (supplierId: number, date: string) => {
    const updatedItems = [...orderItems];
    const indicesToUpdate: number[] = [];

    updatedItems.forEach((item, index) => {
      if (item.supplier_id === supplierId) {
        updatedItems[index] = { ...item, supplier_delivery_date: date };
        indicesToUpdate.push(index);
      }
    });

    setOrderItems(updatedItems);

    // Save all updated items
    indicesToUpdate.forEach(index => {
      saveOrderItem(updatedItems[index], index);
    });
  };

  // Get all asterisk numbers currently in use
  const getUsedAsteriskNumbers = (): number[] => {
    const usedNumbers = new Set<number>();
    orderItems.forEach(item => {
      if (item.note_ref) {
        usedNumbers.add(item.note_ref);
      }
    });
    return Array.from(usedNumbers).sort((a, b) => a - b);
  };

  // Update a specific order note
  const updateOrderNote = (noteNum: number, value: string) => {
    const newNotes = { ...orderNotes, [noteNum]: value };
    setOrderNotes(newNotes);
    setHasUnsavedChanges(true);

    // Auto-save with longer debounce to avoid too many requests
    if (autoSaveTimeout) {
      clearTimeout(autoSaveTimeout);
    }
    const timeout = setTimeout(async () => {
      // Save order notes directly to avoid stale closure issue
      try {
        const endpoint = isProject
          ? `${API_BASE_URL}/projects/${id}`
          : `${API_BASE_URL}/leads/${id}`;

        await axios.put(endpoint, {
          order_notes_json: JSON.stringify(newNotes)
        }, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        setHasUnsavedChanges(false);
      } catch (error) {
        console.error('Error saving order notes:', error);
      }
    }, 1500);
    setAutoSaveTimeout(timeout);
  };

  // Handle tab key - add new row if at last field of last row
  const handleKeyDown = (e: React.KeyboardEvent, index: number, field: string) => {
    if (e.key === 'Tab' && !e.shiftKey && field === 'r' && index === orderItems.length - 1) {
      // Add new row
      e.preventDefault();
      addNewRow();
      
      // Focus on first field of new row after a short delay
      setTimeout(() => {
        const supplierSelect = document.querySelector(`#supplier-${orderItems.length}`) as HTMLElement;
        if (supplierSelect) {
          supplierSelect.focus();
        }
      }, 100);
    }
  };

  // Add new row
  const addNewRow = () => {
    // Only add if last row has some data
    const lastRow = orderItems[orderItems.length - 1];
    if (lastRow && (lastRow.supplier_id || lastRow.category_id || lastRow.qty || lastRow.hxw || lastRow.type || lastRow.l || lastRow.drillings || lastRow.r)) {
      setOrderItems([...orderItems, createEmptyItem()]);
    }
  };

  // Delete row
  const deleteRow = async (index: number) => {
    const item = orderItems[index];

    if (item.id) {
      // Delete from server
      try {
        const deleteUrl = isRemedial
          ? `${API_BASE_URL}/remedials/${id}/order-items/${item.id}`
          : isProject
            ? `${API_BASE_URL}/projects/${id}/order-items/${item.id}`
            : `${API_BASE_URL}/leads/${id}/order-items/${item.id}`;
        
        await axios.delete(deleteUrl, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        showNotification('Order item deleted', 'success');
      } catch (error) {
        console.error('Error deleting order item:', error);
        showNotification('Failed to delete order item', 'error');
        return;
      }
    }
    
    // Remove from state
    const updatedItems = orderItems.filter((_, i) => i !== index);
    
    // Ensure at least one empty row remains
    if (updatedItems.length === 0) {
      updatedItems.push(createEmptyItem());
    }
    
    setOrderItems(updatedItems);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <CircularProgress />
      </Box>
    );
  }

  const entity = isRemedial ? remedial : isProject ? project : lead;

  if (!entity) {
    return (
      <Box p={3}>
        <Typography color="error">{isRemedial ? 'Remedial' : isProject ? 'Project' : 'Lead'} not found</Typography>
      </Box>
    );
  }

  const customerName = isRemedial
    ? (remedial?.customer_first_name && remedial?.customer_last_name
      ? `${remedial.customer_first_name} ${remedial.customer_last_name}`
      : 'Unknown Customer')
    : isProject
      ? (project?.customer_name || 'Unknown Customer')
      : (lead?.customer_first_name && lead?.customer_last_name
        ? `${lead.customer_first_name} ${lead.customer_last_name}`
        : lead?.customer_name || 'Unknown Customer');

  return (
    <Box p={3}>
      {/* Header */}
      <Box display="flex" alignItems="center" gap={2} mb={3}>
        <IconButton onClick={() => navigate(isRemedial ? `/projects/${remedial?.project_id}` : isProject ? `/projects/${id}` : `/leads/${id}/edit`)}>
          <ArrowBack />
        </IconButton>
        <Typography variant="h4" component="h1" flexGrow={1}>
          {isRemedial ? 'REMEDIAL ' : ''}Order Build - {isRemedial ? (remedial?.repair || 'Remedial Issue') : (entity as any).title}
        </Typography>
      </Box>

      {/* Personal Information Box */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Customer Information
          </Typography>
          <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} gap={3}>
            <Box flex={1}>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <Person fontSize="small" color="action" />
                <Typography>{customerName}</Typography>
              </Box>
              {entity.order_number && (
                <Box mb={2}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Order Number
                  </Typography>
                  <Typography variant="h6" color="primary" fontWeight="bold">
                    {entity.order_number}
                  </Typography>
                </Box>
              )}
              {entity.customer_email && (
                <Box display="flex" alignItems="center" gap={1} mb={1}>
                  <Email fontSize="small" color="action" />
                  <Typography variant="body2">{entity.customer_email}</Typography>
                </Box>
              )}
              {entity.customer_phone && (
                <Box display="flex" alignItems="center" gap={1} mb={1}>
                  <Phone fontSize="small" color="action" />
                  <Typography variant="body2">{entity.customer_phone}</Typography>
                </Box>
              )}
              {((lead && (lead.customer_address_1 || lead.customer_town || lead.customer_postcode)) || 
                (project && project.customer_address)) && (
                <Box display="flex" alignItems="center" gap={1}>
                  <LocationOn fontSize="small" color="action" />
                  <Typography variant="body2">
                    {isProject 
                      ? project?.customer_address
                      : [lead?.customer_address_1, lead?.customer_address_2, lead?.customer_town, lead?.customer_postcode]
                          .filter(Boolean)
                          .join(', ')}
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Important Fields */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Order Details
          </Typography>
          <Box display="grid" gridTemplateColumns={{ xs: '1fr', md: 'repeat(4, 1fr)' }} gap={2}>
            <TextField
              label="Customer delivery date"
              type="date"
              value={customerDeliveryDate}
              onChange={(e) => handleMetadataChange('customer_delivery_date', e.target.value)}
              onBlur={handleMetadataBlur}
              fullWidth
              size="small"
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="Door Style"
              value={doorStyle}
              onChange={(e) => handleMetadataChange('door_style', e.target.value)}
              onBlur={handleMetadataBlur}
              fullWidth
              size="small"
            />
            <TextField
              label="Worktop Style"
              value={worktopStyle}
              onChange={(e) => handleMetadataChange('worktop_style', e.target.value)}
              onBlur={handleMetadataBlur}
              fullWidth
              size="small"
            />
            <TextField
              label="Worktop Make"
              value={worktopMake}
              onChange={(e) => handleMetadataChange('worktop_make', e.target.value)}
              onBlur={handleMetadataBlur}
              fullWidth
              size="small"
            />
            <TextField
              label="Worktop Manufacture"
              value={worktopManufacture}
              onChange={(e) => handleMetadataChange('worktop_manufacture', e.target.value)}
              onBlur={handleMetadataBlur}
              fullWidth
              size="small"
            />
            <TextField
              label="Worktop Thickness"
              value={worktopThickness}
              onChange={(e) => handleMetadataChange('worktop_thickness', e.target.value)}
              onBlur={handleMetadataBlur}
              fullWidth
              size="small"
            />
            <TextField
              label="Worktop Size"
              value={worktopSize}
              onChange={(e) => handleMetadataChange('worktop_size', e.target.value)}
              onBlur={handleMetadataBlur}
              fullWidth
              size="small"
            />
            <TextField
              label="Door Colour"
              value={itemColour}
              onChange={(e) => handleMetadataChange('item_colour', e.target.value)}
              onBlur={handleMetadataBlur}
              fullWidth
              size="small"
            />
            <TextField
              label="Door Back Colour"
              value={backColour}
              onChange={(e) => handleMetadataChange('back_colour', e.target.value)}
              onBlur={handleMetadataBlur}
              fullWidth
              size="small"
            />
          </Box>
        </CardContent>
      </Card>

      {/* Order Form Title */}
      <Typography variant="h5" align="center" sx={{ mb: 3, fontWeight: 'bold' }}>
        ORDER FORM
      </Typography>

      {/* Bulk Delivery Date Setter */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
            Set Delivery Date:
          </Typography>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <Select
              value={bulkSupplier}
              onChange={(e) => setBulkSupplier(e.target.value as number | '')}
              displayEmpty
            >
              <MenuItem value="">
                <em>Select Supplier</em>
              </MenuItem>
              {suppliers
                .filter(s => orderItems.some(item => item.supplier_id === s.id))
                .map((supplier) => (
                  <MenuItem key={supplier.id} value={supplier.id}>
                    {supplier.company} ({orderItems.filter(item => item.supplier_id === supplier.id).length} items)
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
          <TextField
            type="date"
            size="small"
            value={bulkDate}
            onChange={(e) => setBulkDate(e.target.value)}
            sx={{ width: 160 }}
            InputLabelProps={{ shrink: true }}
          />
          <Button
            variant="contained"
            size="small"
            disabled={!bulkSupplier || !bulkDate}
            onClick={() => {
              if (bulkSupplier && bulkDate) {
                applyDeliveryDateToSupplier(bulkSupplier, bulkDate);
                setBulkSupplier('');
                setBulkDate('');
              }
            }}
          >
            Apply to All
          </Button>
        </Box>
      </Paper>

      {/* Order Items Matrix */}
      <TableContainer component={Paper} sx={{ mb: 3 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell align="center" sx={{ width: '180px' }}>Supplier</TableCell>
              <TableCell align="center" sx={{ width: '140px' }}>Category</TableCell>
              <TableCell align="center">Details</TableCell>
              <TableCell align="center" sx={{ width: '100px' }}>Code</TableCell>
              <TableCell align="center" sx={{ width: '130px' }}>Supplier Del.</TableCell>
              <TableCell align="center" sx={{ width: '60px' }}>Note</TableCell>
              <TableCell align="center" sx={{ width: '100px' }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {orderItems.map((item, index) => {
              const isWorktop = isWorktopCategory(item.category_id);
              const isDoors = categories.find(c => c.id === item.category_id)?.name === 'Doors';

              return (
                <TableRow
                  key={item.id || `new-${index}`}
                  sx={{
                    backgroundColor: (item as any).remedial_id ? '#fff5f5' : 'inherit'
                  }}
                >
                  <TableCell>
                    <FormControl fullWidth size="small">
                      <Select
                        id={`supplier-${index}`}
                        value={item.supplier_id || ''}
                        onChange={(e) => handleItemChange(index, 'supplier_id', e.target.value)}
                        displayEmpty
                      >
                        <MenuItem value="">
                          <em>Select Supplier</em>
                        </MenuItem>
                        {suppliers.map((supplier) => (
                          <MenuItem key={supplier.id} value={supplier.id}>
                            {supplier.company}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </TableCell>

                  <TableCell>
                    <FormControl fullWidth size="small">
                      <Select
                        id={`category-${index}`}
                        value={item.category_id || ''}
                        onChange={(e) => handleItemChange(index, 'category_id', e.target.value)}
                        displayEmpty
                      >
                        <MenuItem value="">
                          <em>Select Category</em>
                        </MenuItem>
                        {categories.map((category) => (
                          <MenuItem key={category.id} value={category.id}>
                            {category.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </TableCell>

                  <TableCell>
                    {isWorktop ? (
                      // Qty + Multiline textarea + SQ for worktops
                      <Box display="flex" gap={2} alignItems="flex-start">
                        <Box sx={{ width: '80px' }}>
                          <Typography variant="caption" color="text.secondary">Qty</Typography>
                          <TextField
                            value={item.qty}
                            onChange={(e) => handleItemChange(index, 'qty', e.target.value)}
                            size="small"
                            fullWidth
                            inputProps={{ maxLength: 5 }}
                          />
                        </Box>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="caption" color="text.secondary">Description</Typography>
                          <TextField
                            value={item.description || ''}
                            onChange={(e) => handleItemChange(index, 'description', e.target.value)}
                            size="small"
                            fullWidth
                            multiline
                            minRows={1}
                            placeholder="Enter worktop details (press Enter for new line)"
                            sx={{
                              '& .MuiInputBase-inputMultiline': {
                                resize: 'both',
                                overflow: 'auto',
                              }
                            }}
                          />
                        </Box>
                        <Box sx={{ width: '80px' }}>
                          <Typography variant="caption" color="text.secondary">SQ</Typography>
                          <TextField
                            value={item.sq || ''}
                            onChange={(e) => handleItemChange(index, 'sq', e.target.value)}
                            size="small"
                            fullWidth
                          />
                        </Box>
                        <Box sx={{ width: '120px' }}>
                          <Typography variant="caption" color="text.secondary">Colour</Typography>
                          <TextField
                            value={item.colour || ''}
                            onChange={(e) => handleItemChange(index, 'colour', e.target.value)}
                            size="small"
                            fullWidth
                          />
                        </Box>
                      </Box>
                    ) : isDoors ? (
                      // Doors layout with labels above each field
                      <Box display="flex" gap={1} alignItems="flex-start">
                        <Box sx={{ width: '60px' }}>
                          <Typography variant="caption" color="text.secondary">Qty</Typography>
                          <TextField
                            value={item.qty}
                            onChange={(e) => handleItemChange(index, 'qty', e.target.value)}
                            onKeyDown={(e) => handleKeyDown(e, index, 'qty')}
                            size="small"
                            fullWidth
                            inputProps={{ maxLength: 5 }}
                          />
                        </Box>
                        <Box sx={{ width: '100px' }}>
                          <Typography variant="caption" color="text.secondary">HxW</Typography>
                          <TextField
                            value={item.hxw}
                            onChange={(e) => handleItemChange(index, 'hxw', e.target.value)}
                            onKeyDown={(e) => handleKeyDown(e, index, 'hxw')}
                            size="small"
                            fullWidth
                          />
                        </Box>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="caption" color="text.secondary">Type</Typography>
                          <TextField
                            value={item.type}
                            onChange={(e) => handleItemChange(index, 'type', e.target.value)}
                            onKeyDown={(e) => handleKeyDown(e, index, 'type')}
                            size="small"
                            fullWidth
                          />
                        </Box>
                        <Box sx={{ width: '50px' }}>
                          <Typography variant="caption" color="text.secondary">L</Typography>
                          <TextField
                            value={item.l}
                            onChange={(e) => handleItemChange(index, 'l', e.target.value)}
                            onKeyDown={(e) => handleKeyDown(e, index, 'l')}
                            size="small"
                            fullWidth
                            inputProps={{ maxLength: 5 }}
                          />
                        </Box>
                        <Box sx={{ width: '100px' }}>
                          <Typography variant="caption" color="text.secondary">Drillings</Typography>
                          <TextField
                            value={item.drillings}
                            onChange={(e) => handleItemChange(index, 'drillings', e.target.value)}
                            onKeyDown={(e) => handleKeyDown(e, index, 'drillings')}
                            size="small"
                            fullWidth
                          />
                        </Box>
                        <Box sx={{ width: '50px' }}>
                          <Typography variant="caption" color="text.secondary">R</Typography>
                          <TextField
                            value={item.r}
                            onChange={(e) => handleItemChange(index, 'r', e.target.value)}
                            onKeyDown={(e) => handleKeyDown(e, index, 'r')}
                            size="small"
                            fullWidth
                            inputProps={{ maxLength: 5 }}
                          />
                        </Box>
                      </Box>
                    ) : (
                      // Other categories (including Accessories) - Qty, Description, Style, Colour
                      <Box display="flex" gap={2} alignItems="flex-start">
                        <Box sx={{ width: '80px' }}>
                          <Typography variant="caption" color="text.secondary">Qty</Typography>
                          <TextField
                            value={item.qty}
                            onChange={(e) => handleItemChange(index, 'qty', e.target.value)}
                            size="small"
                            fullWidth
                            inputProps={{ maxLength: 5 }}
                          />
                        </Box>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="caption" color="text.secondary">Description</Typography>
                          <TextField
                            value={item.description || ''}
                            onChange={(e) => handleItemChange(index, 'description', e.target.value)}
                            size="small"
                            fullWidth
                            placeholder="Enter details"
                          />
                        </Box>
                        <Box sx={{ width: '120px' }}>
                          <Typography variant="caption" color="text.secondary">Style</Typography>
                          <TextField
                            value={item.style || ''}
                            onChange={(e) => handleItemChange(index, 'style', e.target.value)}
                            size="small"
                            fullWidth
                          />
                        </Box>
                        <Box sx={{ width: '120px' }}>
                          <Typography variant="caption" color="text.secondary">Colour</Typography>
                          <TextField
                            value={item.colour || ''}
                            onChange={(e) => handleItemChange(index, 'colour', e.target.value)}
                            size="small"
                            fullWidth
                          />
                        </Box>
                      </Box>
                    )}
                  </TableCell>

                  <TableCell>
                    <TextField
                      value={item.code || ''}
                      onChange={(e) => handleItemChange(index, 'code', e.target.value)}
                      size="small"
                      fullWidth
                      placeholder="Code"
                    />
                  </TableCell>

                  <TableCell>
                    <TextField
                      type="date"
                      value={item.supplier_delivery_date ? item.supplier_delivery_date.split('T')[0] : ''}
                      onChange={(e) => handleItemChange(index, 'supplier_delivery_date', e.target.value)}
                      size="small"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                    />
                  </TableCell>

                  <TableCell>
                    <FormControl size="small" fullWidth>
                      <Select
                        value={item.note_ref || ''}
                        onChange={(e) => handleItemChange(index, 'note_ref', e.target.value || null)}
                        displayEmpty
                        sx={{ fontSize: '0.875rem' }}
                      >
                        <MenuItem value="">-</MenuItem>
                        {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(num => (
                          <MenuItem key={num} value={num}>*{num}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </TableCell>

                  <TableCell>
                    <Box display="flex" gap={1} justifyContent="center">
                      {index === orderItems.length - 1 && (
                        <IconButton
                          size="small"
                          onClick={addNewRow}
                          color="primary"
                          title="Add Row"
                        >
                          <Add />
                        </IconButton>
                      )}
                      <IconButton
                        size="small"
                        onClick={() => deleteRow(index)}
                        color="error"
                        title="Delete Row"
                      >
                        <Delete />
                      </IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Order Notes Section - shows for asterisked items */}
      {getUsedAsteriskNumbers().length > 0 && (
        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
            Order Notes
          </Typography>
          <Box display="flex" flexDirection="column" gap={2}>
            {getUsedAsteriskNumbers().map(num => (
              <Box key={num} display="flex" alignItems="flex-start" gap={2}>
                <Typography variant="body1" sx={{ minWidth: '30px', fontWeight: 'bold', mt: 1 }}>
                  *{num}
                </Typography>
                <TextField
                  value={orderNotes[num] || ''}
                  onChange={(e) => updateOrderNote(num, e.target.value)}
                  placeholder={`Note for items marked *${num}`}
                  multiline
                  minRows={2}
                  fullWidth
                  size="small"
                />
              </Box>
            ))}
          </Box>
        </Paper>
      )}

      {/* Done Button */}
      <Box display="flex" justifyContent="center">
        <Button
          variant="contained"
          size="large"
          onClick={handleDone}
          disabled={saving}
        >
          {saving ? 'Saving...' : 'Done'}
        </Button>
      </Box>

      {/* Saving indicator */}
      {saving && (
        <Box position="fixed" bottom={20} right={20}>
          <Paper sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <CircularProgress size={20} />
            <Typography variant="body2">Saving...</Typography>
          </Paper>
        </Box>
      )}
    </Box>
  );
}